<template>
  <div class="visibility-settings">
    <div class="visibility-head">
      <div class="friend-feature-row">
        <span class="friend-feature-label">资料可见性</span>
        <el-switch v-model="enabled" :disabled="loading || saving" />
      </div>
      <p class="field-hint">
        关闭时，已开启朋友功能的用户均可看见你的资料。开启后，对方必须同时满足下面全部有效条件才能看见你（已失效的测评条件会跳过）。
      </p>
    </div>

    <div v-if="enabled" v-loading="loading" class="visibility-body">
      <div class="rule-block">
        <div class="rule-block-title">
          <span>基本资料条件</span>
          <el-button type="primary" link @click="addProfileRule">添加条件</el-button>
        </div>
        <p class="field-hint">身高、体重、年龄、收入。比较方式：不低于、不高于、或介于某区间。</p>
        <div v-if="!profileRules.length" class="empty-hint">未设置资料条件（不限制身高/年龄等）</div>
        <div v-for="(rule, idx) in profileRules" :key="'p-' + idx" class="rule-row">
          <el-select v-model="rule.field" class="rule-field" placeholder="字段" @change="resetProfileValues(rule)">
            <el-option
              v-for="opt in profileFieldOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-select v-model="rule.op" class="rule-op" placeholder="比较">
            <el-option
              v-for="opt in opOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <template v-if="rule.field === 'income'">
            <el-select
              v-if="rule.op !== 'lte'"
              v-model="rule.min"
              class="rule-num"
              placeholder="最低"
            >
              <el-option
                v-for="opt in incomeOptions"
                :key="'min-' + opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
            <span v-if="rule.op === 'between'" class="rule-sep">至</span>
            <el-select
              v-if="rule.op !== 'gte'"
              v-model="rule.max"
              class="rule-num"
              placeholder="最高"
            >
              <el-option
                v-for="opt in incomeOptions"
                :key="'max-' + opt.value"
                :label="opt.label"
                :value="opt.value"
              />
            </el-select>
          </template>
          <template v-else>
            <el-input-number
              v-if="rule.op !== 'lte'"
              v-model="rule.min"
              class="rule-num"
              :min="0"
              :max="profileMax(rule.field)"
              :placeholder="rule.op === 'between' ? '最低' : '数值'"
              controls-position="right"
            />
            <span v-if="rule.op === 'between'" class="rule-sep">至</span>
            <el-input-number
              v-if="rule.op !== 'gte'"
              v-model="rule.max"
              class="rule-num"
              :min="0"
              :max="profileMax(rule.field)"
              :placeholder="rule.op === 'between' ? '最高' : '数值'"
              controls-position="right"
            />
          </template>
          <el-button type="danger" link @click="removeProfileRule(idx)">删除</el-button>
        </div>
      </div>

      <div class="rule-block">
        <div class="rule-block-title">
          <span>官方认证测评条件</span>
          <el-button type="primary" link :disabled="!officialExams.length" @click="addExamRule">
            添加条件
          </el-button>
        </div>
        <p class="field-hint">
          新增条件请选官方认证测评。取消认证后仍按成绩比较；测评被删除或下架后该条会标为「已失效」并在匹配时跳过。
        </p>
        <div v-if="!officialExams.length && !examRules.length" class="empty-hint">暂无官方认证测评，请等待管理员认证后再设置</div>
        <div v-else-if="!examRules.length" class="empty-hint">未设置测评条件（不限制测评成绩）</div>
        <div v-for="(rule, idx) in examRules" :key="'e-' + idx" class="rule-row exam-rule-row">
          <el-tag v-if="rule.invalid" type="info" size="small" effect="plain">已失效</el-tag>
          <el-select
            v-model="rule.examId"
            class="rule-exam"
            placeholder="选择测评"
            filterable
            :disabled="!!rule.invalid"
            @change="onExamChange(rule)"
          >
            <el-option
              v-for="exam in examSelectOptions(rule)"
              :key="exam.examId"
              :label="exam.examName"
              :value="exam.examId"
            />
          </el-select>
          <el-select v-model="rule.dimension" class="rule-dim" placeholder="维度" filterable :disabled="!!rule.invalid">
            <el-option
              v-for="dim in examDimensions(rule.examId, rule)"
              :key="dim.key"
              :label="dimLabel(dim)"
              :value="dim.key"
            />
          </el-select>
          <el-select v-model="rule.op" class="rule-op" placeholder="比较" :disabled="!!rule.invalid">
            <el-option
              v-for="opt in opOptions"
              :key="opt.value"
              :label="opt.label"
              :value="opt.value"
            />
          </el-select>
          <el-input-number
            v-if="rule.op !== 'lte'"
            v-model="rule.min"
            class="rule-num"
            :min="0"
            :max="dimMax(rule)"
            :precision="1"
            controls-position="right"
            placeholder="最低分"
            :disabled="!!rule.invalid"
          />
          <span v-if="rule.op === 'between'" class="rule-sep">至</span>
          <el-input-number
            v-if="rule.op !== 'gte'"
            v-model="rule.max"
            class="rule-num"
            :min="0"
            :max="dimMax(rule)"
            :precision="1"
            controls-position="right"
            placeholder="最高分"
            :disabled="!!rule.invalid"
          />
          <el-button type="danger" link @click="removeExamRule(idx)">删除</el-button>
        </div>
      </div>
    </div>

    <div class="visibility-actions">
      <el-button type="primary" :loading="saving" :disabled="loading" @click="saveSettings">
        保存可见性设置
      </el-button>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus'

const INCOME_OPTIONS = [
  { label: '无收入', value: 0 },
  { label: '5k以下', value: 5 },
  { label: '5k-10k', value: 10 },
  { label: '10k-20k', value: 20 },
  { label: '20k-30k', value: 30 },
  { label: '30k-50k', value: 50 },
  { label: '50k-100k', value: 100 },
  { label: '100k-200k', value: 200 },
  { label: '200k-500k', value: 500 },
  { label: '500k以上', value: 1000 }
]

export default {
  name: 'VisibilitySettingsPanel',
  data() {
    return {
      loading: false,
      saving: false,
      enabled: false,
      profileRules: [],
      examRules: [],
      officialExams: [],
      profileFieldOptions: [
        { label: '身高(cm)', value: 'height' },
        { label: '体重(kg)', value: 'weight' },
        { label: '年龄', value: 'age' },
        { label: '收入', value: 'income' }
      ],
      opOptions: [
        { label: '不低于', value: 'gte' },
        { label: '不高于', value: 'lte' },
        { label: '介于', value: 'between' }
      ],
      incomeOptions: INCOME_OPTIONS
    }
  },
  created() {
    this.loadAll()
  },
  methods: {
    authHeaders() {
      return {
        Authorization: `Bearer ${localStorage.getItem('jwt_token')}`,
        'Content-Type': 'application/json'
      }
    },
    profileMax(field) {
      if (field === 'height') return 300
      if (field === 'weight') return 500
      if (field === 'age') return 120
      return 1000
    },
    examById(examId) {
      return this.officialExams.find((e) => Number(e.examId) === Number(examId))
    },
    examSelectOptions(rule) {
      const list = [...this.officialExams]
      const id = Number(rule.examId)
      if (id && !list.some((e) => Number(e.examId) === id)) {
        list.unshift({
          examId: id,
          examName: rule.examName || `测评 #${id}`,
          dimensions: rule.dimension ? [{ key: rule.dimension, name: rule.dimension, maxScore: 0 }] : []
        })
      }
      return list
    },
    examDimensions(examId, rule) {
      const dims = this.examById(examId)?.dimensions || []
      if (dims.length) return dims
      if (rule?.dimension) {
        return [{ key: rule.dimension, name: rule.dimension, maxScore: 0 }]
      }
      return []
    },
    dimLabel(dim) {
      const name = dim.name || dim.key
      if (dim.maxScore) return `${name}（满分 ${dim.maxScore}）`
      return name
    },
    dimMax(rule) {
      const dim = this.examDimensions(rule.examId, rule).find((d) => d.key === rule.dimension)
      const max = Number(dim?.maxScore)
      return max > 0 ? max : 9999
    },
    onExamChange(rule) {
      const dims = this.examDimensions(rule.examId, rule)
      const exists = dims.some((d) => d.key === rule.dimension)
      if (!exists) {
        rule.dimension = dims[0]?.key || ''
      }
      rule.invalid = false
    },
    addProfileRule() {
      this.profileRules.push({ field: 'height', op: 'gte', min: 0, max: 0 })
    },
    resetProfileValues(rule) {
      rule.min = 0
      rule.max = 0
    },
    removeProfileRule(idx) {
      this.profileRules.splice(idx, 1)
    },
    addExamRule() {
      const first = this.officialExams[0]
      this.examRules.push({
        examId: first?.examId || 0,
        dimension: first?.dimensions?.[0]?.key || '',
        op: 'gte',
        min: 0,
        max: 0
      })
    },
    removeExamRule(idx) {
      this.examRules.splice(idx, 1)
    },
    normalizeRule(rule) {
      const min = Number(rule.min) || 0
      const max = Number(rule.max) || 0
      if (rule.op === 'gte') return { ...rule, min, max: 0 }
      if (rule.op === 'lte') return { ...rule, min: 0, max }
      return { ...rule, min, max }
    },
    async loadAll() {
      this.loading = true
      try {
        await Promise.all([this.fetchSettings(), this.fetchExams()])
      } finally {
        this.loading = false
      }
    },
    async fetchSettings() {
      const response = await fetch('/api/v1/user/visibility/get', {
        method: 'POST',
        headers: this.authHeaders(),
        body: '{}'
      })
      const data = await response.json()
      if (data.code === 200 && data.data) {
        this.enabled = !!data.data.enabled
        this.profileRules = (data.data.profileRules || []).map((r) => ({
          field: r.field,
          op: r.op,
          min: Number(r.min) || 0,
          max: Number(r.max) || 0
        }))
        this.examRules = (data.data.examRules || []).map((r) => ({
          examId: Number(r.examId) || 0,
          dimension: r.dimension || '',
          op: r.op,
          min: Number(r.min) || 0,
          max: Number(r.max) || 0,
          invalid: !!r.invalid,
          examName: r.examName || ''
        }))
      } else if (data.code !== 200) {
        ElMessage.error(data.message || '获取可见性设置失败')
      }
    },
    async fetchExams() {
      const response = await fetch('/api/v1/user/visibility/exams', {
        method: 'POST',
        headers: this.authHeaders(),
        body: '{}'
      })
      const data = await response.json()
      if (data.code === 200) {
        this.officialExams = Array.isArray(data.data) ? data.data : []
      } else {
        ElMessage.error(data.message || '获取官方认证测评失败')
      }
    },
    async saveSettings() {
      for (const r of this.profileRules) {
        if (!r.field || !r.op) {
          ElMessage.warning('请完整填写资料条件')
          return
        }
        if (r.op === 'between' && Number(r.min) > Number(r.max)) {
          ElMessage.warning('资料条件区间最小值不能大于最大值')
          return
        }
      }
      for (const r of this.examRules) {
        if (r.invalid) continue
        if (!r.examId || !r.dimension || !r.op) {
          ElMessage.warning('请完整填写测评条件（测评、维度、比较方式）')
          return
        }
        if (r.op === 'between' && Number(r.min) > Number(r.max)) {
          ElMessage.warning('测评条件区间最小值不能大于最大值')
          return
        }
      }
      this.saving = true
      try {
        const response = await fetch('/api/v1/user/visibility/update', {
          method: 'POST',
          headers: this.authHeaders(),
          body: JSON.stringify({
            enabled: this.enabled,
            profileRules: this.profileRules.map((r) => this.normalizeRule(r)),
            examRules: this.examRules.map((r) => this.normalizeRule(r))
          })
        })
        const data = await response.json()
        if (data.code === 200) {
          ElMessage.success(data.message || '保存成功')
          await this.fetchSettings()
        } else {
          ElMessage.error(data.message || '保存失败')
        }
      } catch (error) {
        ElMessage.error('保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.visibility-settings {
  margin-bottom: 20px;
  padding: 12px 16px 16px;
  background: #f5f7fa;
  border-radius: 8px;
}

.friend-feature-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.friend-feature-label {
  font-weight: 600;
  color: #303133;
}

.field-hint {
  margin: 6px 0 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.45;
}

.visibility-body {
  margin-top: 8px;
}

.rule-block {
  margin-top: 12px;
  padding: 12px;
  background: #fff;
  border-radius: 8px;
}

.rule-block-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-weight: 600;
  color: #303133;
}

.empty-hint {
  font-size: 13px;
  color: #909399;
  padding: 8px 0;
}

.rule-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.rule-field,
.rule-op {
  width: 120px;
}

.rule-exam {
  min-width: 160px;
  flex: 1 1 160px;
}

.rule-dim {
  min-width: 140px;
  flex: 1 1 140px;
}

.rule-num {
  width: 140px;
}

.rule-sep {
  color: #909399;
  font-size: 13px;
}

.visibility-actions {
  margin-top: 14px;
}

@media (max-width: 767px) {
  .rule-field,
  .rule-op,
  .rule-num,
  .rule-exam,
  .rule-dim {
    width: 100%;
    min-width: 0;
    flex: 1 1 100%;
  }
}
</style>
